/**
 * OfferPricer — an educated offer for the LOI Sender, shown as a derivation.
 *
 * The rule this component exists to enforce: the number is never presented on
 * its own. Every figure carries the step that produced it and the evidence
 * behind that step, so the user is deciding whether to trust a chain of
 * reasoning rather than whether to trust a magic number.
 *
 * Where the evidence is thin it says so and offers nothing. That is the whole
 * point — an outreach target in a submarket we barely know should return
 * "insufficient evidence", not a confident figure with nothing under it.
 */

import { useState } from "react";
import { priceOffer } from "../../services/offerPricing";

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

export default function OfferPricer({ property, onUse }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [radius, setRadius] = useState(3);
  const [hurdle, setHurdle] = useState("");
  const [override, setOverride] = useState("");

  const rba = Number(property?.building_sf || property?.rba || property?.square_footage) || 0;

  async function run() {
    setBusy(true);
    const r = await priceOffer({
      lat: Number(property?.latitude), lng: Number(property?.longitude),
      rba, market: property?.market,
    }, { radiusMiles: radius, targetYield: hurdle ? Number(hurdle) : null });
    setResult(r);
    setBusy(false);
  }

  const verdictTone = {
    "well supported": "text-[#4f9d76]",
    supported: "text-[#f04f44]",
    thin: "text-[#c8922f]",
    "outside the traded range": "text-[#e93529]",
    "insufficient evidence": "text-[#e93529]",
  };

  return (
    <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg">
      <div className="px-4 py-3 border-b border-[#3a3a3a] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-[13px] font-semibold text-[#f4f4f4]">Price this offer</h4>
          <p className="text-[11px] text-[#8a8a8a] mt-0.5">
            derived from signed comps, our expense lines and traded cap rates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 5].map((r) => (
              <button key={r} onClick={() => setRadius(r)}
                className={`px-2 py-1 rounded-lg text-[11px] ${radius === r
                  ? "bg-[rgba(233,53,41,0.10)] text-[#f04f44] ring-1 ring-[rgba(233,53,41,0.32)]"
                  : "text-[#8a8a8a] hover:text-[#b4b4b4]"}`}>{r} mi</button>
            ))}
          </div>
          <input value={hurdle} onChange={(e) => setHurdle(e.target.value)}
            placeholder="hurdle %"
            className="w-24 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-2 py-1 text-[11px] text-[#f4f4f4] placeholder-[#6a6a6a]" />
          <button onClick={run} disabled={busy || !rba}
            className="px-3 py-1.5 rounded-lg bg-[rgba(233,53,41,0.10)] text-[#f04f44] ring-1 ring-[rgba(233,53,41,0.32)] text-[12px] disabled:opacity-40">
            {busy ? "Working…" : "Derive"}
          </button>
        </div>
      </div>

      {!rba && (
        <p className="px-4 py-4 text-[12px] text-[#8a8a8a]">
          This property has no building area, so an offer cannot be totalled.
        </p>
      )}

      {result && !result.ok && (
        <div className="px-4 py-4">
          <div className="text-[13px] font-semibold text-[#e93529]">Insufficient evidence</div>
          <p className="text-[12px] text-[#8a8a8a] mt-1 leading-relaxed">{result.why}</p>
          <p className="text-[11px] text-[#7a7a7a] mt-2">
            No figure is offered rather than a weak one. Widen the radius, set
            your own hurdle, or price this by hand.
          </p>
          {result.working?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {result.working.map((w, i) => (
                <div key={i} className="text-[11px] text-[#8a8a8a]">
                  <span className="text-[#8a8a8a]">{w.step}:</span> {w.value} — {w.detail}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {result && result.ok && (
        <div>
          <div className="px-4 py-4 flex items-baseline gap-3 flex-wrap border-b border-[#3a3a3a]">
            <span className="text-[30px] leading-none font-semibold tabular-nums text-[#f4f4f4]">
              ${result.offerPsf.toFixed(2)}
            </span>
            <span className="text-[13px] text-[#8a8a8a]">/SF</span>
            <span className="text-[15px] tabular-nums text-[#b4b4b4]">{money(result.totalOffer)}</span>
            <span className={`text-[12px] ${verdictTone[result.verdict] || "text-[#8a8a8a]"}`}>
              {result.verdict}
            </span>
          </div>

          <ol className="divide-y divide-[#303030]">
            {result.working.map((w, i) => (
              <li key={i} className="px-4 py-2.5 flex items-baseline gap-3">
                <span className="w-5 text-[11px] text-[#7a7a7a] tabular-nums">{i + 1}</span>
                <span className="w-32 text-[12px] text-[#8a8a8a] shrink-0">{w.step}</span>
                <span className="w-28 text-[12.5px] tabular-nums text-[#f4f4f4] shrink-0">{w.value}</span>
                <span className="text-[11px] text-[#8a8a8a] leading-relaxed">{w.detail}</span>
              </li>
            ))}
          </ol>

          <div className="px-4 py-3 border-t border-[#3a3a3a] flex items-center gap-2 flex-wrap">
            <input value={override} onChange={(e) => setOverride(e.target.value)}
              placeholder={result.offerPsf.toFixed(2)}
              className="w-28 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-2 py-1.5 text-[12px] text-[#f4f4f4] placeholder-[#6a6a6a]" />
            <button
              onClick={() => onUse?.(
                override ? Number(override) : Number(result.offerPsf.toFixed(2)),
                { derived: result, overridden: !!override })}
              className="px-3 py-1.5 rounded-lg bg-[rgba(79,157,118,0.12)] text-[#7cbb99] ring-1 ring-[rgba(79,157,118,0.35)] text-[12px]">
              Use {override ? `$${override}` : `$${result.offerPsf.toFixed(2)}`}/SF
            </button>
            <span className="text-[11px] text-[#7a7a7a]">
              {override
                ? "Your figure will be recorded as an override of the derived one."
                : "Edit the box to override; the override is recorded as an override."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
